// ─────────────────────────────────────────────────────────────────────────────
// BillingPage
//
// Shows subscription status, upgrade options, and billing management.
// Accessible from account/settings area.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";
import { isSubscribed } from "./SubscriptionGate";

// Single plan: $15/month (Oct 2026). Annual plan discontinued.
const MONTHLY_PRICE_ID = "price_1UM5ssL8HSUTWmPdohLoAS64";

async function createCheckoutSession(priceId) {
  const res = await authFetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
    method: "POST",
    body: JSON.stringify({
      priceId,
      successUrl: `${window.location.origin}/?subscription=success`,
      cancelUrl:  `${window.location.origin}/?subscription=cancelled`,
    }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.url;
}

async function createBillingPortalSession() {
  const res = await authFetch(`${SUPABASE_URL}/functions/v1/stripe-billing-portal`, {
    method: "POST",
    body: JSON.stringify({
      // Return URL includes a query param so the app knows the user is coming
      // back from the Stripe Portal and should refresh the profile cache.
      // Without this, changes made in the portal (cancel, un-cancel, payment
      // method) are invisible until the next background poll or sign-out/in.
      returnUrl: `${window.location.origin}/billing?billing=updated`,
    }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.url;
}

const STATUS_DISPLAY = {
  active:   { label: "Active",      color: C.green },
  trialing: { label: "Free Trial",  color: C.blue  },
  past_due: { label: "Past Due",    color: C.red   },
  canceled: { label: "Cancelled",   color: C.muted },
  inactive: { label: "No subscription", color: C.muted },
};

export function BillingPage({ profile }) {
  const [loading, setLoading] = useState(null);
  const [error,   setError]   = useState(null);

  const subscribed = isSubscribed(profile);
  const status     = profile?.subscription_status || "inactive";
  const statusInfo = STATUS_DISPLAY[status] || STATUS_DISPLAY.inactive;
  const trialEnd   = profile?.subscription_end
    ? new Date(profile.subscription_end).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  // Pending cancellation: user has clicked "Cancel" in the Billing Portal
  // but still has paid access until subscription_end. Stripe represents this
  // as the subscription staying in 'active'/'trialing' with the
  // cancel_at_period_end flag set. When this is true, we show a dedicated
  // callout and suppress the regular "Trial ends" / "Next billing date" line
  // because those phrases would contradict the cancellation.
  const cancellationPending =
    profile?.cancel_at_period_end === true &&
    (status === "active" || status === "trialing");

  // Trial user with no payment method — needs to add card to keep access
  const isTrialingNoCard =
    status === "trialing" &&
    !cancellationPending &&
    !profile?.is_gifted;

  const handleUpgrade = async () => {
    setLoading("monthly");
    setError(null);
    try {
      const url = await createCheckoutSession(MONTHLY_PRICE_ID);
      window.location.href = url;
    } catch (e) {
      setError(e.message);
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setLoading("portal");
    setError(null);
    try {
      const url = await createBillingPortalSession();
      window.location.href = url;
    } catch (e) {
      setError(e.message);
      setLoading(null);
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: "0 4px" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800 }}>Billing</h2>
      <p style={{ color: C.muted, fontSize: 12, marginBottom: 24 }}>
        Manage your Futures OS Pro subscription
      </p>

      {/* Current status */}
      <Card style={{ marginBottom: 16, padding: "20px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
              Subscription Status
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 8, height: 8, borderRadius: "50%",
                background: statusInfo.color,
              }} />
              <span style={{ fontSize: 16, fontWeight: 700, color: statusInfo.color }}>
                {profile?.is_gifted ? "Gifted Access" : statusInfo.label}
              </span>
            </div>

            {/* Cancellation-pending callout — supersedes the regular
                "Trial ends" / "Next billing date" line. */}
            {cancellationPending && trialEnd && (
              <div style={{
                marginTop: 10,
                padding: "10px 12px",
                background: C.yellow + "12",
                border: `1px solid ${C.yellow}40`,
                borderRadius: 8,
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                maxWidth: 380,
              }}>
                <span style={{ color: C.yellow, fontSize: 14, lineHeight: 1.2 }}>⚠</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.yellow }}>
                    Cancels on {trialEnd}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    You'll keep access until this date. Resume anytime in Manage Billing.
                  </div>
                </div>
              </div>
            )}

            {/* Regular status lines — hidden when a cancellation is pending
                so the messaging doesn't contradict itself. */}
            {!cancellationPending && status === "trialing" && trialEnd && (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                Trial ends {trialEnd}
              </div>
            )}
            {!cancellationPending && status === "active" && trialEnd && (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                Next billing date: {trialEnd}
              </div>
            )}
            {status === "canceled" && trialEnd && (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                Access until: {trialEnd}
              </div>
            )}

            {/* Past Due — payment failed, Stripe retrying. Don't show
                "Access until: [old date]" because that date is misleading
                (it's the failed renewal date, now in the past). Instead,
                surface the action the user needs to take. */}
            {status === "past_due" && (
              <div style={{
                marginTop: 10,
                padding: "10px 12px",
                background: C.red + "12",
                border: `1px solid ${C.red}40`,
                borderRadius: 8,
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                maxWidth: 420,
              }}>
                <span style={{ color: C.red, fontSize: 14, lineHeight: 1.2 }}>⚠</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.red }}>
                    Payment failed — update your card
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    Stripe is retrying automatically (up to 2 weeks). Update
                    your payment method in Manage Billing to avoid losing
                    access.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Manage billing button for active subscribers */}
          {subscribed && !profile?.is_gifted && profile?.stripe_customer_id && (
            <button
              onClick={handleManageBilling}
              disabled={loading === "portal"}
              style={{
                padding: "8px 16px", borderRadius: 8,
                background: "transparent",
                border: `1px solid ${C.border}`,
                color: C.text, cursor: "pointer",
                fontSize: 13, fontWeight: 600,
              }}
            >
              {loading === "portal" ? "Loading…" : "Manage Billing →"}
            </button>
          )}
        </div>
      </Card>

      {/* Add payment method CTA — trialing users with no card yet */}
      {isTrialingNoCard && (
        <Card style={{ marginBottom: 16, padding: "20px 24px", border: `1px solid ${C.blue}40`, background: C.blue + "08" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
            <div style={{ fontSize: 28 }}>💳</div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                Add a payment method to keep access
              </div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
                Your free trial {trialEnd ? `ends ${trialEnd}` : "ends soon"}. Add a card now and you
                won't be interrupted — you won't be charged until the trial ends.
              </div>
              <button
                onClick={handleManageBilling}
                disabled={loading === "portal"}
                style={{
                  padding: "10px 20px", borderRadius: 8,
                  background: C.blue, border: "none",
                  color: "#fff", cursor: loading === "portal" ? "default" : "pointer",
                  fontSize: 13, fontWeight: 700,
                  opacity: loading === "portal" ? 0.6 : 1,
                }}
              >
                {loading === "portal" ? "Loading…" : "Add Payment Method →"}
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* What's included */}
      <Card style={{ marginBottom: 16, padding: "20px 24px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 14 }}>
          Futures OS Pro includes
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            ["🔍", "22 behavioral pattern detectors"],
            ["🧠", "AI coaching synthesis"],
            ["📊", "Full analytics dashboard — Performance, Psychology, Context"],
            ["📅", "Monthly Review — detector-delta narrative"],
            ["🎯", "Trade Review — pattern context flags"],
            ["📈", "Sparkline trend history"],
            ["♾️", "Unlimited trades"],
          ].map(([icon, text]) => (
            <div key={text} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: C.sub }}>
              <span>{icon}</span>
              <span>{text}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Upgrade option — shown only when not subscribed AND not trialing */}
      {!subscribed && !isTrialingNoCard && (
        <div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 12, textAlign: "center" }}>
            30-day free trial • No credit card required • Cancel anytime
          </div>
          <div style={{ maxWidth: 280, margin: "0 auto 16px" }}>

            {/* Monthly — single plan */}
            <button
              onClick={handleUpgrade}
              disabled={!!loading}
              style={{
                width: "100%",
                padding: "20px 16px", borderRadius: 10,
                background: C.purple + "15",
                border: `1px solid ${C.purple}`,
                cursor: loading ? "default" : "pointer",
                textAlign: "center",
                opacity: loading && loading !== "monthly" ? 0.5 : 1,
              }}
            >
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Monthly</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: C.purple, fontFamily: "monospace" }}>$15</div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>per month</div>
              <div style={{
                padding: "8px 16px", borderRadius: 6,
                background: loading === "monthly" ? C.border : C.purple,
                color: "#fff", fontSize: 13, fontWeight: 700,
              }}>
                {loading === "monthly" ? "Loading…" : "Start Free Trial"}
              </div>
            </button>
          </div>

        </div>
      )}

      {error && (
        <div style={{
          marginTop: 12, padding: "10px 14px",
          background: C.red + "10", border: `1px solid ${C.red}30`,
          borderRadius: 8, fontSize: 13, color: C.red,
        }}>
          ⚠ {error}
        </div>
      )}
    </div>
  );
}
